const TICK_MS = 2000;
const BATCH_MAX_CHARS = 12_000;
const NEW_BATCHES_PER_TICK = 3;
const RESENDS_PER_TICK = 2;
const RESEND_AFTER_MS = 8000;
const MAX_UNACKED = 400;
const ACK_PERIOD_MS = 3000;
const MAX_SEEN_SEQS = 4000;
const encoder = new TextEncoder();
const decoder = new TextDecoder();
function parse(data) {
    try {
        const parsed = JSON.parse(decoder.decode(data));
        return parsed.k === 'batch' || parsed.k === 'ack' ? parsed : undefined;
    }
    catch {
        return undefined;
    }
}
/** reporter side */
export class PeerDiagnosticsManager {
    opts;
    now;
    rid;
    unacked = new Map();
    nextSeq = 1;
    reachable = false;
    timer;
    inFlight = false;
    lostUnacked = 0;
    constructor(opts) {
        this.opts = opts;
        this.now = opts.now ?? (() => Date.now());
        this.rid = opts.rid ?? Math.random().toString(36).slice(2, 10);
    }
    getRid() {
        return this.rid;
    }
    start() {
        this.timer ??= setInterval(() => {
            this.tick().catch(() => undefined);
        }, TICK_MS);
    }
    stop() {
        if (this.timer !== undefined)
            clearInterval(this.timer);
        this.timer = undefined;
    }
    /** somebody else on the control partition has been heard from */
    setReachable(reachable) {
        this.reachable = reachable;
    }
    getUnackedCount() {
        return this.unacked.size;
    }
    onNetworkMessage(data) {
        const message = parse(data);
        if (message?.k !== 'ack' || message.rid !== this.rid)
            return;
        for (const seq of message.seqs)
            this.unacked.delete(seq);
    }
    async tick() {
        if (!this.reachable || this.inFlight)
            return;
        this.inFlight = true;
        try {
            const now = this.now();
            let resent = 0;
            for (const entry of this.unacked.values()) {
                if (resent >= RESENDS_PER_TICK)
                    break;
                if (now - entry.lastSentAt < RESEND_AFTER_MS)
                    continue;
                entry.message.resend += 1;
                entry.message.sentAt = now;
                entry.lastSentAt = now;
                await this.send(entry.message);
                resent += 1;
            }
            for (let i = 0; i < NEW_BATCHES_PER_TICK; i += 1) {
                const lines = this.opts.drain(BATCH_MAX_CHARS);
                if (lines.length === 0)
                    break;
                const message = {
                    k: 'batch',
                    rid: this.rid,
                    seq: this.nextSeq,
                    sentAt: now,
                    resend: 0,
                    dropped: this.opts.queueState().dropped + this.lostUnacked,
                    lines: lines.map((l) => [l.t, l.src, l.lvl, l.msg]),
                };
                this.nextSeq += 1;
                this.unacked.set(message.seq, { message, lastSentAt: now });
                if (this.unacked.size > MAX_UNACKED) {
                    const oldest = this.unacked.keys().next().value;
                    if (oldest !== undefined) {
                        this.lostUnacked +=
                            this.unacked.get(oldest)?.message.lines.length ?? 0;
                        this.unacked.delete(oldest);
                    }
                }
                await this.send(message);
            }
        }
        finally {
            this.inFlight = false;
        }
    }
    async send(message) {
        try {
            await this.opts.publish(encoder.encode(JSON.stringify(message)));
        }
        catch {
            // stays unacked: sent again later
        }
    }
}
/** receiving side: prints what reporters send, acknowledges it */
export class PeerDiagnosticsSink {
    opts;
    now;
    seen = new Map();
    pendingAcks = new Map();
    timer;
    constructor(opts) {
        this.opts = opts;
        this.now = opts.now ?? (() => Date.now());
    }
    start() {
        this.timer ??= setInterval(() => {
            this.flushAcks().catch(() => undefined);
        }, ACK_PERIOD_MS);
    }
    stop() {
        if (this.timer !== undefined)
            clearInterval(this.timer);
        this.timer = undefined;
    }
    onNetworkMessage(publisherId, data) {
        const message = parse(data);
        if (message?.k !== 'batch')
            return;
        const from = publisherId.slice(0, 10);
        let seen = this.seen.get(message.rid);
        if (!seen) {
            seen = new Set();
            this.seen.set(message.rid, seen);
        }
        // acknowledge duplicates too: the reporter evidently missed the ack
        const acks = this.pendingAcks.get(message.rid) ?? [];
        acks.push(message.seq);
        this.pendingAcks.set(message.rid, acks);
        if (seen.has(message.seq))
            return;
        seen.add(message.seq);
        if (seen.size > MAX_SEEN_SEQS) {
            const first = seen.values().next().value;
            if (first !== undefined)
                seen.delete(first);
        }
        this.opts.log(`[peer-diag-batch] ${JSON.stringify({
            rid: message.rid,
            from,
            seq: message.seq,
            sentAt: message.sentAt,
            recvAt: this.now(),
            resend: message.resend,
            dropped: message.dropped,
            n: message.lines.length,
        })}`);
        for (const [t, src, lvl, msg] of message.lines) {
            this.opts.log(`[peer-diag] ${JSON.stringify({ rid: message.rid, t, src, lvl, msg })}`);
        }
    }
    async flushAcks() {
        for (const [rid, seqs] of this.pendingAcks) {
            if (seqs.length === 0)
                continue;
            this.pendingAcks.set(rid, []);
            const ack = { k: 'ack', rid, seqs };
            try {
                await this.opts.publish(encoder.encode(JSON.stringify(ack)));
            }
            catch {
                // the reporter resends, we acknowledge again
            }
        }
    }
}
